/**
 * What a report page reports to PostHog, and the one place its identity is decided.
 *
 * PostHog is already running site-wide — `instrumentation-client.ts` initialises
 * it, and with `defaults: '2025-11-30'` that resolves `capture_pageview` to
 * `'history_change'` and leaves `autocapture` on. So report pages ALREADY send
 * `$pageview` with the full `$current_url`, and already send an `$autocapture`
 * click for the Calendly link. Nothing here is rescuing lost data.
 *
 * What it adds is two things autocapture cannot give:
 *
 * 1. **A stable name for the conversion.** `$autocapture` identifies a click by
 *    its DOM — and each of these fifteen reports was authored by hand with its
 *    own class names (`sp-btn`, `rt-btn`, `rz-btn`, ...). A funnel built on that
 *    breaks the next time someone renames a class, silently, in a way that looks
 *    like the CTA stopped converting.
 * 2. **A property you can group by.** Answering "who read SPM's report" from
 *    `$current_url` means string-matching URLs; `report_slug` is a property.
 *
 * The event these pages CANNOT produce is `booking_scheduled`. That one comes
 * from `CalendlyAnalyticsListener`, which hears the embedded Calendly popup's
 * postMessage. Every report CTA is a plain link to calendly.com in a new tab, so
 * the booking happens on Calendly's own origin and the listener never hears it.
 * `report_cta_clicked` is therefore the last event a report can observe: it is
 * intent, not a booking, and it must not be read as one.
 */

/** Every event this directory emits. Add here or it is not a report event. */
export const REPORT_VIEWED = "report_viewed";
export const REPORT_CTA_CLICKED = "report_cta_clicked";

/** Report subjects, not the identity/company of the person reading them.
 * UUIDs must come from Revenue Brain's accounts/report association; never
 * manufacture one from a slug or infer the reader's company from this table.
 * Verified read-only against Revenue Brain on 15 September 2026.
 */
const REPORT_ACCOUNTS: Readonly<Record<string, { account_domain: string; account_id?: string }>> = {
  "topgeo-ai": {
    account_domain: "topgeo.ai",
    account_id: "67ea62fe-4eac-4f31-9e8f-adbfb64f0ee9",
  },
  "zylensolutions-com": {
    account_domain: "zylensolutions.com",
    account_id: "eb815c82-2e87-4e05-b564-44418b5b9118",
  },
  "snu-chennai": {
    account_domain: "snuchennai.edu.in",
    account_id: "8db1c5e0-279e-4a9f-94ed-e5d0955a1bdd",
  },
};

export function reportAccountProperties(slug: string | null): { account_domain?: string; account_id?: string } {
  if (!slug || !Object.hasOwn(REPORT_ACCOUNTS, slug)) return {};
  return { ...REPORT_ACCOUNTS[slug] };
}

/**
 * How a report identifies itself, and the ONE place to change that.
 *
 * These pages are about real, named companies who did not ask to be audited,
 * and they carry `robots: noindex`. `/reports/spmfg-com` is Special Products &
 * Mfg. by name, so this function decides how identifiable our analytics makes
 * a prospect.
 *
 * Today it returns the slug, which holds nothing PostHog is not already
 * receiving in `$current_url` on every pageview. If that turns out to be more
 * than we want to hold, this is the single line to change — return a hash, a
 * bucket, or the constant `'report'` — and every event in this directory
 * coarsens with it. Nothing else reads the pathname.
 */
export function reportSlugFromPath(pathname: string | null): string | null {
  if (!pathname) return null;
  const match = /^\/reports\/([^/]+)/.exec(pathname);
  return match ? match[1] : null;
}

/**
 * Whether PostHog is actually up.
 *
 * Pull-request preview builds get the key from GitHub secrets, but a local
 * `next build` without a `.env` does not — `posthog.init` is then called with
 * `undefined` and capturing would warn on every interaction. A report page must
 * render and work identically with no analytics behind it, so every call site
 * checks this first rather than assuming.
 */
export function analyticsReady(posthog: { __loaded?: boolean }): boolean {
  return Boolean(posthog.__loaded);
}
